import { get, set } from 'idb-keyval';
import { makeAutoObservable } from 'mobx';

import { DISABLE_IDLE_OBSERVER_VALUE, SESSION_TIMEOUT_IN_SECONDS } from '../config/Environment';

interface IdleObserverParams {
	readonly idleTimeoutInMilliseconds: number;
}

export class IdleObserver {
	private idleTimeoutInMilliseconds: number;

	private constructor({ idleTimeoutInMilliseconds }: IdleObserverParams) {
		this.setIdleTimeoutInMilliseconds(idleTimeoutInMilliseconds);
		makeAutoObservable(this);
	}

	static readonly create = (params: IdleObserverParams): IdleObserver => new IdleObserver(params);

	readonly setIdleTimeoutInMilliseconds = (idleTimeoutInSeconds: typeof this.idleTimeoutInMilliseconds): void => {
		if (idleTimeoutInSeconds === DISABLE_IDLE_OBSERVER_VALUE) {
			this.idleTimeoutInMilliseconds = DISABLE_IDLE_OBSERVER_VALUE;
			return;
		}
		this.idleTimeoutInMilliseconds = idleTimeoutInSeconds * 1000;
	};

	readonly setLastActivityTimestamp = async (): Promise<void> => {
		await set('lastActivityTimestamp', Date.now());
	};

	readonly initialize = async (): Promise<void> => {
		await this.setLastActivityTimestamp();
	};

	readonly isIdle = async (): Promise<boolean> => {
		if (this.idleTimeoutInMilliseconds === DISABLE_IDLE_OBSERVER_VALUE) {
			return false;
		}
		const lastActivityTimestamp = await get<number>('lastActivityTimestamp');
		const now = Date.now();

		if (!lastActivityTimestamp) {
			return false;
		}

		if (now - lastActivityTimestamp < this.idleTimeoutInMilliseconds) {
			return false;
		}

		return true;
	};
}

let maybeIdleObserver: IdleObserver | null = null;

export const getOrCreateIdleObserver = (): IdleObserver => {
	maybeIdleObserver ??= IdleObserver.create({
		idleTimeoutInMilliseconds: SESSION_TIMEOUT_IN_SECONDS,
	});

	return maybeIdleObserver;
};
